import { createSelector } from 'reselect';

import { getSegmentsValues as getCheckoutSegmentsValues } from '../../../../../Checkout/store/order/selectors';

import { AnyStateWithExchange, ExchangeSegmentValue } from '../types';
import { transformSegmentToParams } from '../../lib/transformSegmentToParams';

export const getSegmentsState = (state: AnyStateWithExchange) => state.exchange.segments;

export const getSelectedSegmentsIds = createSelector(getSegmentsState, segmentsState =>
	Object.entries(segmentsState).reduce<string[]>(
		(ids, [id, entry]) => [...ids, ...(entry.isSelected ? [id] : [])],
		[]
	)
);

export const getIsAllSegmentsSelected = createSelector(getSegmentsState, segments =>
	Object.values(segments).every(segment => segment.isSelected)
);

export const getSegments = createSelector(
	getSegmentsState,
	getCheckoutSegmentsValues,
	(segmentsState, segmentsValues) =>
		segmentsValues.map<ExchangeSegmentValue>(segment => ({
			...segment,
			...segmentsState[segment.id]
		}))
);

export const getSelectedSegments = createSelector(getSegments, segments =>
	segments.filter(segment => segment.isSelected)
);

export const getSelectedSegmentsParams = createSelector(getSelectedSegments, selectedSegments =>
	selectedSegments.map(transformSegmentToParams)
);

export const getSelectedSegmentsDates = createSelector(getSelectedSegments, selectedSegments =>
	selectedSegments.map(segment => segment.selectedDate ?? segment.date)
);

export const getIsAllSelectedSegmentsWithDate = createSelector(
	getSelectedSegments,
	selectedSegments => selectedSegments.length && selectedSegments.every(segment => segment.selectedDate)
);

export const getSegmentsControls = createSelector(
	getSegments,
	getSelectedSegmentsDates,
	(segments, selectedSegmentsDates) => {
		const unselectedDateSegmentIndex = segments.findIndex(segment => segment.isSelected && !segment.selectedDate);

		return segments.map((segment, index) => {
			const prevSegment = segments[index - 1];
			const prevSegmentDate = prevSegment?.selectedDate ?? prevSegment?.date ?? new Date();

			return {
				...segment,
				minDate: prevSegmentDate,
				segmentSearchParams: transformSegmentToParams(segment),
				selectedDates: selectedSegmentsDates,
				isOpen: unselectedDateSegmentIndex === index
			};
		});
	}
);
